import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { toDateKey } from "../components/admin/events/eventTypes";

const emptyForm = { event_type: "", date: "", start_time: "", end_time: "", client_name: "", notes: "" };

/** All state/handlers for the Admin > Calendario de Eventos tab: CRUD + date filter + dialog. */
export const useEventCRUD = (onEventsChanged) => {
  const [events, setEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/admin/events").then((r) => setEvents(r.data)), []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    if (selectedDate) setForm({ ...emptyForm, date: toDateKey(selectedDate) });
    setDialogOpen(true);
  };

  const openEdit = (event) => {
    setEditingId(event.id);
    setForm({
      event_type: event.event_type,
      date: event.date,
      start_time: event.start_time,
      end_time: event.end_time,
      client_name: event.client_name,
      notes: event.notes || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.event_type || !form.date || !form.start_time || !form.end_time || !form.client_name.trim()) {
      toast.error("Completa el tipo de evento, fecha, horario y nombre del cliente");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/admin/events/${editingId}`, form);
        toast.success("Evento actualizado");
      } else {
        await api.post("/admin/events", form);
        toast.success("Evento creado");
      }
      setDialogOpen(false);
      resetForm();
      await load();
      onEventsChanged?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar el evento");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Evento eliminado");
      await load();
      onEventsChanged?.();
    } catch {
      toast.error("Error al eliminar el evento");
    }
  };

  const filteredEvents = selectedDate
    ? events.filter((e) => e.date === toDateKey(selectedDate))
    : events;

  return {
    events, selectedDate, setSelectedDate, filteredEvents,
    dialogOpen, setDialogOpen, form, setForm, editingId, saving,
    openCreate, openEdit, submit, remove, resetForm,
  };
};
